import { useCallback, useEffect, useRef, useState } from "react";
import {
  Card,
  CardBody,
  Input,
  Button,
  Typography,
} from "@material-tailwind/react";
import { FaEyeSlash } from "react-icons/fa";
import { BiSolidShow } from "react-icons/bi";
import { resetUserPassword } from "../../api/axiosInstanceApi";
import { toast, ToastContainer } from "react-toastify";
import { messages } from "../../utils/constantMessages";
import { useNavigate, useOutletContext } from "react-router-dom";
import { get_mentee_data } from "../../utils/common";

function successToast(successMessage: string) {
  toast.success(successMessage, {
    toastId: 1001,
    position: "top-center",
    autoClose: 2000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "light",
  });
}

function notifyError(errorMassage: string) {
  toast.error(errorMassage, {
    position: "top-center",
    autoClose: 5000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "light",
  });
}

function ResetPasswordPage() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [complexPasswordError, setComplexPasswordError] = useState([]);
  const [isCurrentPasswordIncorrect, setIsCurrentPasswordIncorrect] =
    useState(false);
  const navigate = useNavigate();
  const isSidebarOpen = useOutletContext();

  const [CorrectPasswordErrorMessage, setCorrectPasswordErrorMessage] =
    useState("");
  // const [ setIsBothPasswordSame] = useState(false);
  const [showPasswordGuidelines, setShowPasswordGuidelines] = useState(false);

  const timeoutRef = useRef<number | null>(null);
  const isSubmittingRef = useRef(false);
  // Password validation rules
  const passwordRules = [
    {
      regex: /.{8,}/,
      message: "Password must be at least 8 characters long.",
      id: "length",
    },
    {
      regex: /[A-Z]/,
      message: "Password must contain at least one uppercase letter.",
      id: "uppercase",
    },
    {
      regex: /[a-z]/,
      message: "Password must contain at least one lowercase letter.",
      id: "lowercase",
    },
    {
      regex: /[0-9]/,
      message: "Password must contain at least one digit.",
      id: "digit",
    },
    {
      regex: /[!@#$%^&*_\-+=?]/,
      message:
        "Password must contain at least one special character (!@#$%^&*_-+=?).",
      id: "special",
    },
    {
      regex: /^\S*$/,
      message: "Password must not contain spaces.",
      id: "spaces",
    },
  ];

  const checkPasswordStrength = (password: string) => {
    const results = passwordRules.map((rule) => ({
      ...rule,
      isValid: rule.regex.test(password),
    }));

    const validCount = results.filter((r) => r.isValid).length;
    const strength = Math.floor((validCount / passwordRules.length) * 100);

    // return { results, strength };
    return { results, strength, allValid: validCount === passwordRules.length };
  };

  const handleNewPasswordFocus = () => {
    setComplexPasswordError([]);
    setShowPasswordGuidelines(true);
  };

  const handleNewPasswordBlur = () => {
    setTimeout(() => {
      if (!document.activeElement?.closest(".new-password-section")) {
        setShowPasswordGuidelines(false);
      }
    }, 100);
  };

  const handleReset = async () => {
    if (isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;

    try {
      const userEmail = get_mentee_data().email;
      if (newPassword !== confirmPassword && newPassword.length > 0) {
        notifyError(messages.RESET_PASSWORD.RESET_PASSWORD_MISMATCH);
        isSubmittingRef.current = false;
        return;
      }

      if (
        currentPassword !== "" &&
        newPassword !== "" &&
        confirmPassword !== "" &&
        userEmail !== null
      ) {
        await resetUserPassword(userEmail, currentPassword, newPassword);
        setComplexPasswordError([]);
        setIsCurrentPasswordIncorrect(false);
        setNewPassword("");
        setCurrentPassword("");
        setConfirmPassword("");
        successToast(messages.RESET_PASSWORD.RESET_PASSWORD_SUCCESSFULLY);
        setTimeout(() => {
          navigate("/");
        }, 3000);
      }
    } catch (error: any) {
      console.log("error: ", error);
      if (error.status === 400) {
        if (error.response.data.error === "WRONG_PASSWORD") {
          setIsCurrentPasswordIncorrect(true);
          setComplexPasswordError([]);
          setCorrectPasswordErrorMessage(error.response.data.message);
        } else {
          // setIsBothPasswordSame(false);
          setIsCurrentPasswordIncorrect(false);
          setComplexPasswordError(error.response.data.errors);
        }
      } else {
        notifyError(error.response.data.message);
      }
    } finally {
      isSubmittingRef.current = false;
    }
  };

  useEffect(() => {
    // console.log("name: ", name)
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const debouncedOnClick = useCallback(() => {
    if (isSubmittingRef.current) {
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      if (!isSubmittingRef.current) {
        handleReset();
      }
    }, 300);
  }, [handleReset]);

  const { results: passwordResults, strength: passwordStrength } =
    checkPasswordStrength(newPassword);
  const handleCopyChange = (e: any) => {
    e.preventDefault();
  };
  return (
    <div className="flex items-center sm:pt-0 justify-center h-full">
      {String(isSidebarOpen) == "false" && <div className="text-white sidebar-height"></div>}
      <Card className="w-full max-w-md shadow-lg">
        <CardBody>
          <h2 className="text-xl font-bold mb-6 text-center">
            Change Your Password
          </h2>
          <form className="flex flex-col h-full">
            <div className="flex flex-col gap-4">
              {/* Current Password Field */}
              <div>
                <Typography
                  variant="small"
                  color="blue-gray"
                  className="mb-2 font-bold border-none"
                >
                  Current Password
                </Typography>
                <div className="relative">
                  <Input
                    type={showCurrentPassword ? "text" : "password"}
                    placeholder="Enter your current password"
                    className="w-full p-3 rounded-md pr-8 outline-1"
                    value={currentPassword}
                    autoFocus
                    onChange={(e) => {
                      setIsCurrentPasswordIncorrect(false);
                      setCurrentPassword(e.target.value);
                    }}
                  />
                  {showCurrentPassword ? (
                    <FaEyeSlash
                      className="absolute w-5 h-5 top-2.5 right-2.5 cursor-pointer"
                      onClick={() =>
                        setShowCurrentPassword(!showCurrentPassword)
                      }
                    />
                  ) : (
                    <BiSolidShow
                      className="absolute w-5 h-5 top-2.5 right-2.5 cursor-pointer"
                      onClick={() =>
                        setShowCurrentPassword(!showCurrentPassword)
                      }
                    />
                  )}
                  {isCurrentPasswordIncorrect && (
                    <div className="text-red-600 text-sm">
                      {CorrectPasswordErrorMessage}
                    </div>
                  )}
                </div>
              </div>

              {/* New Password Field */}
              <div className="new-password-section">
                <Typography
                  variant="small"
                  color="blue-gray"
                  className="mb-2 font-bold order-none"
                >
                  New Password
                </Typography>
                <div className="relative">
                  <Input
                    type={showNewPassword ? "text" : "password"}
                    placeholder="Enter your new password"
                    className="w-full p-3 pr-8 rounded-md outline-1"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      // const { allValid } = checkPasswordStrength(newPassword);
                      // setAllGuidelinesMet(allValid);
                    }}
                    onFocus={handleNewPasswordFocus}
                    onBlur={handleNewPasswordBlur}
                  />
                  {showNewPassword ? (
                    <FaEyeSlash
                      className="absolute w-5 h-5 top-2.5 right-2.5 text-slate-600 cursor-pointer"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    />
                  ) : (
                    <BiSolidShow
                      className="absolute w-5 h-5 top-2.5 right-2.5 text-slate-600 cursor-pointer"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    />
                  )}
                  {complexPasswordError.length > 0 && (
                    <div className="text-red-600 text-sm">
                      {complexPasswordError.map((_, index) => (
                        <p key={index}>{complexPasswordError[index]}</p>
                      ))}
                    </div>
                  )}
                  {currentPassword === newPassword &&
                    newPassword.length > 0 &&
                    currentPassword.length > 0 && (
                      <div className="text-red-600 text-sm">
                        {messages.RESET_PASSWORD.RESET_SAME_PASSWORD}
                      </div>
                    )}
                </div>
              </div>

              {/* Password Guidelines */}
              {currentPassword !== newPassword &&
                showPasswordGuidelines &&
                newPassword.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {/* <div
                    className="h-1 w-full bg-gray-200 rounded-full overflow-hidden"
                    // role="progressbar"
                    aria-valuenow={passwordStrength}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label="Password strength"
                  >
                    <div
                      className="h-full bg-green-500 transition-all duration-300"
                      style={{ width: `${passwordStrength}%` }}
                    />
                   
                  </div> */}
                    <div className="space-y-1">
                      {passwordResults.map((result) => (
                        <div
                          key={result.id}
                          className={`flex items-center text-sm ${
                            result.isValid ? "text-green-600" : "text-gray-500"
                          }`}
                        >
                          <span
                            className={`mr-2 ${
                              result.isValid
                                ? "text-green-600"
                                : "text-gray-500"
                            }`}
                          >
                            {result.isValid ? "✓" : "○"}
                          </span>
                          <span>{result.message}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Confirm Password Field */}
              <div>
                <Typography
                  variant="small"
                  color="blue-gray"
                  className="mb-2 font-bold order-none"
                >
                  Confirm New Password
                </Typography>
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Re-enter your new password"
                    className="w-full p-3 pr-8 rounded-md outline-1"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onCopy={handleCopyChange}
                    onPaste={handleCopyChange}
                  />
                  {showConfirmPassword ? (
                    <FaEyeSlash
                      className="absolute w-5 h-5 top-2.5 right-2.5 text-slate-600 cursor-pointer"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    />
                  ) : (
                    <BiSolidShow
                      className="absolute w-5 h-5 top-2.5 right-2.5 text-slate-600 cursor-pointer"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    />
                  )}
                  {newPassword !== confirmPassword &&
                    newPassword.length > 0 &&
                    confirmPassword.length > 0 && (
                      <div className="text-red-600 text-sm">
                        {messages.RESET_PASSWORD.RESET_PASSWORD_MISMATCH}
                      </div>
                    )}
                </div>
              </div>
            </div>

            {/* Spacer div to push the button to the bottom */}
            <div className="flex-grow relative"></div>

            <div className="flex justify-center pt-10">
              <Button
                size="lg"
                className="w-[50vh] p-3 bg-blue-800 hover:bg-blue-700 text-gray-200"
                onClick={debouncedOnClick}
                disabled={
                  !currentPassword ||
                  !newPassword ||
                  !confirmPassword ||
                  (newPassword !== confirmPassword && newPassword.length > 0) ||
                  (currentPassword === newPassword &&
                    newPassword.length > 0 &&
                    currentPassword.length > 0) ||
                  passwordStrength !== 100 // Add this condition
                }
              >
                Change
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
      <ToastContainer />
    </div>
  );
}

export default ResetPasswordPage;
